import { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { cn } from 'helpers/classnames';
import { ReturnStepInitiate } from 'components/account/Returns/ReturnStepInitiate';
import ReturnRefundInfo from 'components/account/Returns/ReturnRefundInfo';
import { logError } from 'middleware/logger';
import { getOrderAddress } from 'helpers/MyAccountUtils';
import ReturnsUtils from 'helpers/ReturnsUtils';
import { fetchRewardsInfoV2 } from 'actions/account/rewards';
import {
  fetchAccountInfo,
  fetchZawPromotionsAndAddOrderAmounts,
  resetCancelOrReturnItems,
  resetZawState,
  setPreviousOrderAction
} from 'actions/account/account';
import returnsActions from 'actions/account/returns';
import { fetchAllAddresses, getNewAddressId, updateMailingAddressOrName, updateShippingPhone } from 'actions/account/shipping';
import { fetchOrderDetails, getTransportationOptions } from 'actions/account/order';
import { PageLoader } from 'components/Loader';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import RefundSummary from 'components/account/Returns/RefundSummary';
import ReturnProductCards from 'components/account/Returns/ReturnProductCards';
import ReturnLegalVerbiage from 'components/account/Returns/ReturnLegalVerbiage';
import OrderInfo from 'components/account/Returns/OrderInfo';
import { ReturnStep } from 'constants/returnSteps';
import { PROVIDE_LABEL_OPTIONS, PROVIDE_REFUND_OPTIONS } from 'constants/exchanges';
import { ORDER_ACTION_NONE, ORDER_ACTION_RETURN } from 'constants/orderActions';
import { RETURN_STEP } from 'constants/amethystEnums';
import { ERROR_INELIGIBLE_RETURN_DENIED } from 'middleware/fetchErrorMiddleware';
import { RETURN_REASON_MISSING_ACCEPT_TERMS_OF_RETURN_ERROR, RETURN_REASONS_HAVE_NOT_BEEN_SELECTED_ERROR } from 'constants/returnReasons';
import windowUtils from 'utils/window';
import returnsUtils from 'utils/returns';
import zaw from 'utils/zaw';
import { track } from 'apis/amethyst';
import { evSubmitReturnClick } from 'events/returns';
import { EMAIL_TOKEN_COOKIE } from 'constants/cookies';
import { removeFromStoredCookies } from 'actions/session';
import { removeCookie } from 'helpers/index';
import { getIsWithinReturnWindow } from 'helpers/dateUtils';
import { ReturnStepConfirmation } from 'components/account/Returns/ReturnStepConfirmation';
import { FEATURE_CHECKOUT_STORE_CREDIT_FORCE_START_DATE } from 'constants/features';
import { useIsEnrolledInVariant } from 'hooks/unleash/useIsEnrolledInVariant';
import { useStoreCreditEnabled } from 'hooks/useStoreCreditEnabled';
import { fireReturnStepAmplitudeEvent } from 'utils/amplitude';
import refundSummaryUtils from 'utils/refundSummary';
import { AMPLITUDE_RETURNS_STEP_NAME } from 'constants/amplitude';

// eslint-disable-next-line css-modules/no-unused-class
import css from 'styles/containers/returns/returns.scss';

export class Returns extends Component {
  constructor(props) {
    super(props);
    this.rofsInput = createRef();
    this.state.labelPaymentType = props.isGuest || this.context?.is6pm ? PROVIDE_REFUND_OPTIONS.ORIGINAL_PAYMENT : null;
  }

  state = {
    labelDelivery: PROVIDE_LABEL_OPTIONS.PRINT,
    labelPaymentType: null,
    isLabelless: false,
    orderIds: [],
    ordersMap: {},
    startTime: 0,
    acceptTermsOfReturn: false
  };

  componentDidMount() {
    const {
      authentication,
      fetchAccountInfo,
      fetchAllAddresses,
      fetchSpecificReturnInfo,
      fetchRewardsInfoV2,
      fetchOrderDetails,
      account: { cancelOrReturnItems: items, customerInfo },
      getTransportationOptions
    } = this.props;

    const {
      router,
      marketplace: {
        features: { showAccountRewards },
        returns: { handlesReturnShipping, showReturnInstructionsBoxes, noDefaultLabelDelivery },
        is6pm
      }
    } = this.context;

    this.setState({ startTime: new Date().getTime() });

    if (noDefaultLabelDelivery) {
      this.setState({ labelDelivery: null });
    }

    if (!handlesReturnShipping) {
      this.setState({ labelDelivery: PROVIDE_LABEL_OPTIONS.ANY_CARRIER });
    }

    // Set labelPaymentType to original payment for 6pm marketplace
    if (is6pm && this.state.labelPaymentType === null) {
      this.setState({ labelPaymentType: PROVIDE_REFUND_OPTIONS.ORIGINAL_PAYMENT });
    }

    if (authentication) {
      fetchAllAddresses();
    }
    if (showReturnInstructionsBoxes && !customerInfo) {
      fetchAccountInfo();
    }

    if (!items.length) {
      router.replace('/returns');
    }

    const orderIds = [];

    items.forEach(({ orderId }) => {
      if (!orderIds.includes(orderId)) {
        orderIds.push(orderId);
        const orderItems = items.filter(order => order.orderId === orderId);
        const orderItemAsins = orderItems.map(({ product }) => product.asin);

        Promise.all([
          fetchOrderDetails(orderId),
          fetchSpecificReturnInfo(orderId, orderItemAsins),
          showAccountRewards && fetchRewardsInfoV2({ includesBenefits: true, orderId })
        ])
          .then(([orderDetail]) => {
            this.setState(
              state => ({
                ordersMap: { ...state.ordersMap, [orderId]: orderDetail }
              }),
              () => {
                this.sendReturnStepAmplitudeEvent(AMPLITUDE_RETURNS_STEP_NAME.REVIEW);
              }
            );
          })
          .then(() => {
            getTransportationOptions();
          })
          .catch(err => logError(err));
      }
    });

    this.setState({ orderIds });
  }

  componentDidUpdate(prevProps) {
    const prevSubmitError = prevProps?.returns?.isReturnPreventingSubmitError;
    const currentSubmitError = this.props?.returns?.isReturnPreventingSubmitError;

    // When isReturnPreventingSubmitError changes from false to true
    if (!prevSubmitError && currentSubmitError) {
      windowUtils.scrollToTop();
    }

    if (this.isZAWReturnsEnabled()) {
      this.fetchZawPromotionsOnce();
      this.setZawSubsidyReissueEligibility();
    }
  }

  componentWillUnmount() {
    const {
      resetCancelOrReturnItems,
      resetReturnsInState,
      resetZawState,
      account: { previousOrderAction },
      removeFromStoredCookies
    } = this.props;

    if (previousOrderAction !== ORDER_ACTION_RETURN) {
      resetCancelOrReturnItems();
    }
    resetReturnsInState();
    resetZawState();
    removeFromStoredCookies(EMAIL_TOKEN_COOKIE);
  }

  onLabelDeliverySelect = event => {
    this.setState({ labelDelivery: event.target.value }, () => {
      this.sendReturnStepAmplitudeEvent(AMPLITUDE_RETURNS_STEP_NAME.SELECT_RETURN_METHOD);
    });
  };

  onLabelPaymentTypeSelect = value => {
    this.setState({ labelPaymentType: value }, () => {
      this.sendReturnStepAmplitudeEvent(AMPLITUDE_RETURNS_STEP_NAME.SELECT_REFUND_METHOD);
    });
  };

  onSelectReturnReason = ({ currentTarget }) => {
    const {
      clearReturnError,
      updateBoxInfoWithReturnReason,
      returns: { boxInfo }
    } = this.props;
    const unitId = currentTarget.getAttribute('data-unitid');
    const boxNum = currentTarget.getAttribute('data-boxnum');

    const [reasonCode] = currentTarget.value.split(':');

    updateBoxInfoWithReturnReason(unitId, reasonCode, boxInfo, boxNum);
    clearReturnError();
    this.sendReturnStepAmplitudeEvent(AMPLITUDE_RETURNS_STEP_NAME.SELECT_REASON);
  };

  onCheckTermsOfReturn = () => {
    const { clearReturnError } = this.props;
    this.setState({ acceptTermsOfReturn: !this.state.acceptTermsOfReturn });
    clearReturnError();
  };

  onCancelReturn = () => {
    this.context.router.push(`/orders/${this.props.order.orderId}`);
    this.sendReturnStepAmplitudeEvent(AMPLITUDE_RETURNS_STEP_NAME.CANCEL);
  };

  onSubmitReturn = () => {
    this.preventCustomerFromReturningTheSameItems();
    this.props.returnSubmitInitiate();
    windowUtils.scrollToTop();
    this.makeSureReturnHasNotBeenSubmittedAndThenSubmit();
    this.sendReturnStepAmplitudeEvent(AMPLITUDE_RETURNS_STEP_NAME.SUBMIT);
  };

  /**
   * To avoid customers from returning the same item(s),
   * we reset the previousAction to NONE to avoid the order detail page from picking up the old return selections
   * which is allowing customers to proceed to return initiate page with the same item(s) again.
   * Or there'll be too many 400 errors triggered.
   */
  preventCustomerFromReturningTheSameItems = () => {
    this.props.setPreviousOrderAction(ORDER_ACTION_NONE);
  };

  sendReturnStepAmplitudeEvent = returnStepName => {
    const { labelDelivery, ordersMap, labelPaymentType } = this.state;
    const {
      account: { cancelOrReturnItems: zawPromotions, customerInfo },
      rewards: { rewardsInfo } = {},
      returns: { boxInfo, contractIds },
      sessionId
    } = this.props;

    const combinedBoxTotals = refundSummaryUtils.combineBoxTotals(
      Object.values(boxInfo).map(box => refundSummaryUtils.makeTotals(box.items, this.isZAWReturnsEnabled(), zawPromotions, ordersMap))
    );

    fireReturnStepAmplitudeEvent(
      ReturnsUtils.buildReturnStepAmplitudeEvent({
        boxInfo,
        refundTotal: combinedBoxTotals.refundTotal,
        contractIds,
        returnMethod: labelDelivery,
        refundMethod: labelPaymentType,
        returnStepName,
        sessionId,
        userInfo: {
          directedID: customerInfo?.directedId,
          vip_status: rewardsInfo?.isVipOrConsented ? 'enrolled' : 'unenrolled',
          zaw_status: combinedBoxTotals.zawDiscounts > 0 ? 'enrolled' : 'unenrolled'
        }
      })
    );
  };

  /**
   * There seem to be a good number of cases where items being returned are already in a return status.
   * Before submitting the return, let's make a call for the order details and compare the return status.
   */
  makeSureReturnHasNotBeenSubmittedAndThenSubmit = () => {
    const { labelDelivery, labelPaymentType } = this.state;

    const {
      account: { cancelOrReturnItems: items },
      returns: { boxInfo },
      rewards: { rewardsInfo } = {},
      fetchOrderDetails,
      returnSubmitError
    } = this.props;
    const { benefits: { refundOnFirstScan } = {} } = rewardsInfo || {};

    const orderIdsToCheck = new Set();
    items.forEach(({ orderId }) => orderIdsToCheck.add(orderId));

    const orderCheckPromises = [];
    const orderIds = [];
    // Compare items on a PER ORDER basis
    orderIdsToCheck.forEach(orderId => {
      orderIds.push(orderId);
      const returnItemsInOrder = items.filter(returnItem => returnItem.orderId === orderId);
      const orderCheckPromise = Promise.resolve(fetchOrderDetails(orderId)).then(returnsUtils.checkIfReturnAlreadySubmitted(returnItemsInOrder));
      orderCheckPromises.push(orderCheckPromise);
    });

    track(() => [evSubmitReturnClick, { returnLabel: labelDelivery, refundMethod: labelPaymentType, startTime: this.state.startTime }]);

    Promise.all(orderCheckPromises).then(hasReturnErrorValues => {
      const duplicateErrorWasSubmitted = hasReturnErrorValues.includes(true);
      if (duplicateErrorWasSubmitted) {
        returnSubmitError({ function: 'orderCheckPromises', orderIds, refundOnFirstScan, labelDelivery });
      } else {
        const numOfBoxes = Object.keys(boxInfo).length;
        this.fetchContractIds().then(allContractIds => {
          this.makeSubmitReturnCalls(numOfBoxes, allContractIds);
        });
      }
    });
  };

  fetchContractIds = () => {
    const {
      account: { cancelOrReturnItems },
      returns: { contractIds, boxInfo },
      fetchAdditionalContractId
    } = this.props;
    const numOfBoxes = Object.keys(boxInfo).length;
    const numOfContractIds = contractIds.length;

    const needsAdditionalContractIds = numOfBoxes > numOfContractIds;
    if (!needsAdditionalContractIds) {
      return Promise.resolve(contractIds);
    }

    const {
      orderId,
      product: { asin }
    } = cancelOrReturnItems[0];
    const additionalContractIdCalls = [];
    for (let i = numOfContractIds; i < numOfBoxes; i++) {
      additionalContractIdCalls.push(fetchAdditionalContractId(orderId, asin));
    }

    return Promise.all(additionalContractIdCalls).then(newContractIds => contractIds.concat(newContractIds));
  };

  makeSubmitReturnCalls = (numOfBoxes, contractIds) => {
    const {
      returns: { boxInfo },
      submitReturn,
      returnSubmitComplete,
      returnSubmitError,
      getNewAddressId,
      shipping: { address },
      isRecognizedCustomer
    } = this.props;
    const { labelDelivery } = this.state;
    const { cookieDomain } = this.context.marketplace;

    // this is where the magic happens, build returnSubmit object using contractIds + box object in state
    const returnCalls = addressId => {
      const isLabelless = labelDelivery === PROVIDE_LABEL_OPTIONS.LABELLESS;

      this.setState({ isLabelless });

      const returnSubmitCalls = [];
      let promosToReissueAlreadyUsedInOtherBoxes = [];
      for (let i = 0; i < numOfBoxes; i++) {
        const submitReturnPayload = this.buildReturnInfoObject({
          contractId: contractIds[i],
          items: boxInfo[i + 1].items,
          addressId,
          promosToReissueAlreadyUsedInOtherBoxes
        });

        promosToReissueAlreadyUsedInOtherBoxes = [
          ...promosToReissueAlreadyUsedInOtherBoxes,
          ...submitReturnPayload.promotions.map(promotion => promotion.promotionId)
        ];
        returnSubmitCalls.push(submitReturn(submitReturnPayload));
      }

      Promise.all(returnSubmitCalls)
        .then(() => {
          // Remove the email token cookie when completing a guest return as a logged in user.
          if (isRecognizedCustomer) {
            removeCookie(EMAIL_TOKEN_COOKIE, cookieDomain);
          }
          returnSubmitComplete();
        })
        .catch(e => {
          if (e.id === ERROR_INELIGIBLE_RETURN_DENIED) {
            this.context.router.push('/error/ccBlocked');
          } else {
            returnSubmitError({
              function: 'returnSubmitCalls',
              isLabelless,
              numOfBoxes,
              contractIds
            });
          }
        });
    };

    const needsNewAddressIdBeforeSubmission = this.state.labelDelivery === PROVIDE_LABEL_OPTIONS.MAIL;
    if (needsNewAddressIdBeforeSubmission) {
      getNewAddressId(
        address,
        resp => {
          const { addressId } = resp || {};
          returnCalls(addressId);
        },
        { forceOriginal: true }
      );
    } else {
      returnCalls();
    }
  };

  buildReturnInfoObject = ({ contractId, items, addressId, promosToReissueAlreadyUsedInOtherBoxes }) => {
    const { labelDelivery: returnLabelMethod, ordersMap, zawSubsidyReissueEligibility, labelPaymentType } = this.state;
    const {
      account: { zawPromotions },
      isStoreCreditEnabled
    } = this.props;

    const refundToEGC = isStoreCreditEnabled && labelPaymentType === PROVIDE_REFUND_OPTIONS.STORE_CREDIT ? true : undefined;

    const submissionItems = items.map(item => {
      const { unitId, reasonCode } = item;
      return { unitId, reasonCode };
    });

    const promotions = zaw.shouldReissuePromos(this.isZAWReturnsEnabled(), zawSubsidyReissueEligibility)
      ? zaw.extractReissuablePromosForBox(items, ordersMap, zawPromotions, promosToReissueAlreadyUsedInOtherBoxes)
      : [];

    const submitReturnPayload = {
      contractId,
      submissionItems,
      returnLabelMethod,
      promotions
    };

    if (refundToEGC) {
      submitReturnPayload.refundToEGC = refundToEGC;
    }

    if (returnLabelMethod === PROVIDE_LABEL_OPTIONS.MAIL) {
      submitReturnPayload.addressId = addressId; // backend requires both addressId and snailMailAddressId for now
      submitReturnPayload.snailMailAddressId = addressId;
      submitReturnPayload.notSendNotifications = false;
    }

    return submitReturnPayload;
  };

  makeRefundSummary = ({ useCustomerReturnableFlag, isConfirmation }) => {
    const { isShipmentOptionsError, isRepetitionConflictError, firstName } = this.props;
    const { step, boxInfo } = this.props.returns;
    const { zawPromotions, isRequestingZawPromotions } = this.props.account;
    const { returnPolicyUrl, showReturnInstructionsBoxes } = this.context.marketplace.returns;
    const { authentication, ordersMap, zawSubsidyReissueEligibility, labelDelivery } = this.state;
    const CLTPhoneNumber = this.getCLTPhoneNumber();
    const isZAWReturnsEnabled = this.isZAWReturnsEnabled();
    const itemsMissingReturnReason = this.itemsMissingReturnReason();
    const boxEls = [];
    if (boxInfo) {
      const singleBox = Object.keys(boxInfo).length === 1;

      for (const box in boxInfo) {
        const { items } = boxInfo[box];
        boxEls.push(
          <div className={cn(css.cols, css.initiate)} key={box}>
            <div className={cn(css.leftCol, { [css.leftColFlex]: showReturnInstructionsBoxes })}>
              <ReturnProductCards
                step={step}
                boxNum={box}
                items={items}
                singleBox={singleBox}
                phoneNumber={CLTPhoneNumber}
                isShipmentOptionsError={isShipmentOptionsError}
                isRepetitionConflictError={isRepetitionConflictError}
                onSelectReturnReason={this.onSelectReturnReason}
                useCustomerReturnableFlag={useCustomerReturnableFlag}
                itemsMissingReturnReason={itemsMissingReturnReason}
              />
              <ReturnRefundInfo showReturnInstructionsBoxes={showReturnInstructionsBoxes} firstName={firstName} labelDelivery={labelDelivery} />
            </div>
            <div className={cn(css.rightCol, { [css.rightColFlex]: showReturnInstructionsBoxes })}>
              <RefundSummary
                items={items}
                boxNum={box}
                singleBox={singleBox}
                isZAWReturnsEnabled={isZAWReturnsEnabled}
                zawPromotions={zawPromotions}
                ordersMap={ordersMap}
                isLoadingZawPromotions={isRequestingZawPromotions}
                phoneNumber={CLTPhoneNumber}
                zawSubsidyReissueEligibility={zawSubsidyReissueEligibility}
                returnStep={step === ReturnStep.DONE ? RETURN_STEP.SUBMIT : RETURN_STEP.INITIATE}
              />
              {showReturnInstructionsBoxes && <ReturnLegalVerbiage className={css.legalVerbiageContainer} returnPolicyUrl={returnPolicyUrl} />}
              {isConfirmation && (
                <div className={css.returnSummaryButtons}>
                  <a className={css.continueShoppingButton} href="/">
                    Continue Shopping
                  </a>
                  {authentication && (
                    <a className={css.backToOrderButton} href="/orders">
                      Back to Order History
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      }
    }
    return boxEls;
  };

  makeWhiteLabelRefundSummary = ({ useCustomerReturnableFlag, rmaId, isConfirmationOrder = false }) => {
    const { order, isShipmentOptionsError, isRepetitionConflictError } = this.props;
    const { step, boxInfo } = this.props.returns;
    const { zawPromotions, isRequestingZawPromotions } = this.props.account;
    const { handlesReturnShipping } = this.context.marketplace.returns;
    const { ordersMap, zawSubsidyReissueEligibility } = this.state;
    const CLTPhoneNumber = this.getCLTPhoneNumber();
    const isZAWReturnsEnabled = this.isZAWReturnsEnabled();
    const itemsMissingReturnReason = this.itemsMissingReturnReason();

    if (boxInfo) {
      for (const box in boxInfo) {
        const { items } = boxInfo[box];
        const { orderDate, orderId } = order;

        return (
          <div
            className={
              isConfirmationOrder ? cn(css.cols, css.initiate, css.whiteLabelConfirmationContainer) : cn(css.cols, css.initiate, css.flexContainer)
            }
            key={box}
          >
            {isConfirmationOrder && <div>Return Confirmation {rmaId}</div>}
            <div className={isConfirmationOrder ? css.orderInfo : cn(css.flexItem, css.orderInfo)}>
              <OrderInfo orderDate={orderDate} orderId={orderId} />
            </div>
            <div className={isConfirmationOrder ? css.leftCol : cn(css.leftCol, css.flexItem)}>
              {isConfirmationOrder && <h3>Items you are returning</h3>}
              <ReturnProductCards
                step={step}
                boxNum={box}
                items={items}
                singleBox={true}
                phoneNumber={CLTPhoneNumber}
                isPrivateLabel={true}
                isShipmentOptionsError={isShipmentOptionsError}
                isRepetitionConflictError={isRepetitionConflictError}
                onSelectReturnReason={this.onSelectReturnReason}
                useCustomerReturnableFlag={useCustomerReturnableFlag}
                itemsMissingReturnReason={itemsMissingReturnReason}
              />
            </div>
            <div className={isConfirmationOrder ? css.rightCol : cn(css.rightCol, css.flexItem)}>
              <RefundSummary
                items={items}
                boxNum={box}
                singleBox={true}
                phoneNumber={CLTPhoneNumber}
                isZAWReturnsEnabled={isZAWReturnsEnabled}
                zawPromotions={zawPromotions}
                ordersMap={ordersMap}
                isLoadingZawPromotions={isRequestingZawPromotions}
                zawSubsidyReissueEligibility={zawSubsidyReissueEligibility}
                isPrivateLabel={true}
                handlesReturnShipping={handlesReturnShipping}
                returnStep={step === ReturnStep.DONE ? RETURN_STEP.SUBMIT : RETURN_STEP.INITIATE}
              />
            </div>
          </div>
        );
      }
    }
  };

  fetchZawPromotionsOnce = () => {
    if (this.allOrdersWereFetched()) {
      const { account, fetchZawPromotionsAndAddOrderAmounts } = this.props;
      if (zaw.shouldFetchPromotions(account)) {
        const { ordersMap } = this.state;
        const promotionIds = zaw.extractPromoIds(ordersMap);
        fetchZawPromotionsAndAddOrderAmounts(promotionIds, ordersMap);
      }
    }
  };

  allOrdersWereFetched = () => {
    const { ordersMap, orderIds } = this.state;
    return Object.keys(ordersMap).length === orderIds.length && orderIds.length !== 0;
  };

  setZawSubsidyReissueEligibility = () => {
    const { account } = this.props;
    const { ordersMap, zawSubsidyReissueEligibility } = this.state;
    const isZAWReturnsEnabled = this.isZAWReturnsEnabled();
    if (zaw.shouldSetZawSubsidyReissueEligibility(isZAWReturnsEnabled, account, zawSubsidyReissueEligibility)) {
      const subsidyReissueEligibility = zaw.getZawSubsidyReissueEligibility(account, ordersMap);
      this.setState({ zawSubsidyReissueEligibility: subsidyReissueEligibility });
    }
  };

  isZAWReturnsEnabled = () => zaw.isZAWReturnsEnabled(this.context);

  breadcrumbsLinks = () => {
    const {
      order,
      returns: { preReturnInfo }
    } = this.props;
    return returnsUtils.getBreadcrumbsLinks(order, preReturnInfo);
  };

  findItemsMissingReturnReason = () => returnsUtils.findAllItemsMissingReturnReason(this.props.returns.boxInfo);

  itemsMissingReturnReason = () => {
    const { isReturnPreventingSubmitError } = this.props.returns;
    return isReturnPreventingSubmitError ? this.findItemsMissingReturnReason() : [];
  };

  validateAndSubmit = () => {
    const someMissingReasons = this.findItemsMissingReturnReason().length > 0;
    const { setReturnErrorType, returnPreventingSubmitError, isFlaggedAsCostlyCustomer } = this.props;

    if (someMissingReasons) {
      setReturnErrorType(RETURN_REASONS_HAVE_NOT_BEEN_SELECTED_ERROR);
      returnPreventingSubmitError();
    } else if (isFlaggedAsCostlyCustomer && this.state.acceptTermsOfReturn === false) {
      setReturnErrorType(RETURN_REASON_MISSING_ACCEPT_TERMS_OF_RETURN_ERROR);
      returnPreventingSubmitError();
    } else {
      this.onSubmitReturn();
    }
  };

  getCLTPhoneNumber = () => {
    const { phoneNumber, phoneNumberVip } = this.context.marketplace;
    return this.props.isVip ? phoneNumberVip : phoneNumber;
  };

  getUPSPickUpAddress = () => {
    const { order, returnSourceAddress, shipping } = this.props;
    const { shippingAddress } = order;
    return returnSourceAddress ? ReturnsUtils.formatAddress(returnSourceAddress) : getOrderAddress(shippingAddress, shipping);
  };

  render() {
    const { order, returns, isFlaggedAsCostlyCustomer, isStoreCreditEnabled, forceStoreCreditStartDate, isGuest } = this.props;
    const { step, postReturnInfo, isReturnSubmitting } = returns;
    if (!order || isReturnSubmitting || !this.allOrdersWereFetched()) {
      return <PageLoader centered={true} />;
    }

    const { labelDelivery, zawSubsidyReissueEligibility, labelPaymentType, isLabelless } = this.state;
    const { marketplace, testId } = this.context;

    const isWithinReturnWindow = getIsWithinReturnWindow({
      orderDate: order.orderDate,
      windowLength: marketplace.returns.storeCreditEnabledReturnWindowLength,
      forceStoreCreditStartDate
    });

    const isZAWReturnsEnabled = this.isZAWReturnsEnabled();
    const breadcrumbsLinks = this.breadcrumbsLinks();
    const UPSPickUpAddress = this.getUPSPickUpAddress();

    return (
      <SiteAwareMetadata>
        {step !== ReturnStep.DONE ? (
          <ReturnStepInitiate
            labelPaymentType={labelPaymentType}
            {...this.props}
            isWithinReturnWindow={isWithinReturnWindow}
            testId={testId}
            marketplace={marketplace}
            labelDelivery={labelDelivery}
            zawSubsidyReissueEligibility={zawSubsidyReissueEligibility}
            CLTPhoneNumber={this.getCLTPhoneNumber()}
            UPSPickUpAddress={UPSPickUpAddress}
            isStoreCreditEnabled={isStoreCreditEnabled}
            isZAWReturnsEnabled={isZAWReturnsEnabled}
            breadcrumbsLinks={breadcrumbsLinks}
            makeWhiteLabelRefundSummary={this.makeWhiteLabelRefundSummary}
            makeRefundSummary={this.makeRefundSummary}
            rofsInput={this.rofsInput}
            onChangeFirstScanSelect={this.onChangeFirstScanSelect}
            onLabelDeliverySelect={this.onLabelDeliverySelect}
            onLabelPaymentTypeSelect={this.onLabelPaymentTypeSelect}
            onCancelReturn={this.onCancelReturn}
            onSubmitReturn={this.validateAndSubmit}
            isFlaggedAsCostlyCustomer={isFlaggedAsCostlyCustomer}
            acceptTermsOfReturn={this.state.acceptTermsOfReturn}
            onCheckTermsOfReturn={this.onCheckTermsOfReturn}
            isGuest={isGuest}
          />
        ) : (
          <ReturnStepConfirmation
            {...this.props}
            labelPaymentType={labelPaymentType}
            marketplace={marketplace}
            rmaId={postReturnInfo?.[0]?.rmaId}
            UPSPickUpAddress={UPSPickUpAddress}
            labelDelivery={labelDelivery}
            isLabelless={isLabelless}
            zawSubsidyReissueEligibility={zawSubsidyReissueEligibility}
            isZAWReturnsEnabled={isZAWReturnsEnabled}
            breadcrumbsLinks={breadcrumbsLinks}
            makeWhiteLabelRefundSummary={this.makeWhiteLabelRefundSummary}
            makeRefundSummary={this.makeRefundSummary}
          />
        )}
      </SiteAwareMetadata>
    );
  }
}

Returns.contextTypes = {
  router: PropTypes.object,
  marketplace: PropTypes.object,
  testId: PropTypes.func
};

function WithHooks(Component) {
  const ComponentWithHooks = props => {
    const { isStoreCreditEnabled } = useStoreCreditEnabled();
    const { isEnrolled: forceStoreCreditStartDate } = useIsEnrolledInVariant({
      featureFlag: FEATURE_CHECKOUT_STORE_CREDIT_FORCE_START_DATE,
      isZapposOnly: false
    });
    return <Component isStoreCreditEnabled={isStoreCreditEnabled} forceStoreCreditStartDate={forceStoreCreditStartDate} {...props} />;
  };

  ComponentWithHooks.fetchDataOnServer = Component.fetchDataOnServer;
  ComponentWithHooks.webpackChunkName = Component.webpackChunkName || Component.displayName || Component.name;
  ComponentWithHooks.displayName = `WithHooks(${Component.displayName || Component.name})`;
  return ComponentWithHooks;
}

function mapStateToProps(state) {
  const {
    account,
    authentication,
    cookies,
    customer: isRecognizedCustomer,
    holmes,
    order: { order, shipmentOptions, isShipmentOptionsError, isShipmentOptionsLoading, isRepetitionConflictError, returnSourceAddress },
    returns,
    shipping,
    rewards,
    killswitch: { showAvailableReturnLabels, keepExchangeOrdersFromReturnCancel, arePickupReturnsAuthorized, showPackagelessReturn }
  } = state;
  const firstName = holmes?.firstName || '';
  const isVip = rewards?.rewardsInfo?.isVipOrConsented;
  const akitaPickup = rewards?.rewardsInfo?.benefits?.upsPickup;
  const rofs = rewards?.rewardsInfo?.benefits?.refundOnFirstScan;
  const isFlaggedAsCostlyCustomer = !!rewards?.rewardsInfo?.benefits?.costlyCustomer;
  const isGuest = !!cookies[EMAIL_TOKEN_COOKIE];
  const sessionId = cookies['session-id'];

  return {
    account,
    authentication,
    order,
    returnSourceAddress,
    returns,
    shipping,
    rewards,
    firstName,
    isVip,
    akitaPickup,
    shipmentOptions,
    isShipmentOptionsError,
    isShipmentOptionsLoading,
    isRepetitionConflictError,
    showAvailableReturnLabels,
    keepExchangeOrdersFromReturnCancel,
    arePickupReturnsAuthorized,
    showPackagelessReturn,
    rofs,
    isFlaggedAsCostlyCustomer,
    isRecognizedCustomer,
    isGuest,
    sessionId
  };
}

const connector = connect(mapStateToProps, {
  fetchOrderDetails,
  fetchRewardsInfoV2,
  getTransportationOptions,
  getNewAddressId: getNewAddressId,
  updateMailingAddressOrName: updateMailingAddressOrName,
  updateShippingPhone: updateShippingPhone,
  fetchAllAddresses: fetchAllAddresses,
  fetchAccountInfo: fetchAccountInfo,
  resetCancelOrReturnItems: resetCancelOrReturnItems,
  setPreviousOrderAction: setPreviousOrderAction,
  fetchZawPromotionsAndAddOrderAmounts: fetchZawPromotionsAndAddOrderAmounts,
  resetZawState: resetZawState,
  fetchSpecificReturnInfo: returnsActions.fetchSpecificReturnInfo,
  stepSelection: returnsActions.stepSelection,
  submitReturn: returnsActions.submitReturn,
  fetchAdditionalContractId: returnsActions.fetchAdditionalContractId,
  resetReturnsInState: returnsActions.resetReturnsInState,
  returnSubmitInitiate: returnsActions.returnSubmitInitiate,
  returnSubmitComplete: returnsActions.returnSubmitComplete,
  fetchReturnLabelInfo: returnsActions.fetchReturnLabelInfo,
  returnSubmitError: returnsActions.returnSubmitError,
  clearReturnError: returnsActions.clearReturnError,
  updateBoxInfoWithReturnReason: returnsActions.updateBoxInfoWithReturnReason,
  setReturnErrorType: returnsActions.setReturnErrorType,
  returnPreventingSubmitError: returnsActions.returnPreventingSubmitError,
  removeFromStoredCookies
});

const ReturnsWithHooks = connector(WithHooks(Returns));
export default ReturnsWithHooks;
